import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from 'lucide-react';
const PREVIEW_IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/o8BZ6WAVU6urVthEewM1XN/8B88BEC1-CDB7-4EB3-BC01-467620D19590.webp',
  caption: 'El Cuento del Tío Digital — Estafas modernas explicadas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gksazdmMATdbGuEeemMRaD/A87BBF3F-FF84-4B93-B5E0-138F7235CAD9.webp',
  caption: '¿Qué es el Emprendedurismo? — Fundamentos y tipos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/56x1ocxVL5KLuZHVAWNvNH/7D5C35C7-D441-40C6-A7D8-08A4FB6BD7F3.webp',
  caption: '¿Por Qué Invertir No Es Solo Para Ricos? — Mitos y realidad'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8HZC2hNpdy1ACCHvkM8qtF/D0F13E71-7CB4-4659-AF11-21F8AF39AE7E.webp',
  caption: 'El Impacto Psicológico de la Deuda'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/behbQ3bMtmPA9acJMKpoeZ/48776EC7-EA2C-4A03-93E4-E69587C797FC.webp',
  caption: 'Actividad Integradora — Diseñando un Producto Financiero'
}];
export function KitPreview() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const total = PREVIEW_IMAGES.length;
  const goTo = (newIndex: number, dir: number) => {
    setDirection(dir);
    setIndex((newIndex + total) % total);
  };
  const next = () => goTo(index + 1, 1);
  const prev = () => goTo(index - 1, -1);
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  return <section className="bg-gray-50 py-5 md:py-7 px-4 border-t border-gray-100" data-id="element-279">
      <div className="max-w-3xl mx-auto" data-id="element-280">
        <div className="text-center mb-4" data-id="element-281">
          <div className="inline-flex items-center gap-1.5 bg-[#22c55e]/10 text-[#22c55e] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2" data-id="element-282">
            <EyeIcon className="w-3 h-3" data-id="element-283" />
            Vista previa del material
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-1" data-id="element-284">
            👀 Mirá algunas páginas del kit
          </h2>
          <p className="text-gray-600 text-sm md:text-base" data-id="element-285">
            Así son los contenidos que vas a usar en clase
          </p>
        </div>

        {/* Carousel */}
        <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-286">
          <div className="relative aspect-[3/4] md:aspect-[4/3] overflow-hidden bg-gray-100" data-id="element-287">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-288">
              <motion.img key={index} src={PREVIEW_IMAGES[index].src} alt={PREVIEW_IMAGES[index].caption} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 280,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="absolute inset-0 w-full h-full object-contain bg-white" data-id="element-289" />
            </AnimatePresence>

            {/* Prev/Next buttons */}
            <button onClick={prev} aria-label="Página anterior" className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-md flex items-center justify-center text-gray-700 hover:bg-white hover:text-[#22c55e] transition-colors active:scale-95" data-id="element-290">
              <ChevronLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-291" />
            </button>
            <button onClick={next} aria-label="Página siguiente" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-md flex items-center justify-center text-gray-700 hover:bg-white hover:text-[#22c55e] transition-colors active:scale-95" data-id="element-292">
              <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-293" />
            </button>

            {/* Counter */}
            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs font-bold px-2.5 py-1 rounded-full backdrop-blur" data-id="element-294">
              {index + 1} / {total}
            </div>
          </div>

          {/* Caption */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white" data-id="element-295">
            <p className="text-sm md:text-base text-gray-700 font-medium text-center" data-id="element-296">
              {PREVIEW_IMAGES[index].caption}
            </p>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-3" data-id="element-297">
          {PREVIEW_IMAGES.map((_, i) => <button key={i} onClick={() => goTo(i, i > index ? 1 : -1)} aria-label={`Ir a página ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-[#22c55e]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} data-id="element-298" />)}
        </div>
      </div>
    </section>;
}